/*
 * like lodash, but not!
 */
import orderBy from "lodash/orderBy";
import remove from "lodash/remove";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import identity from "lodash/identity";
import pickBy from "lodash/pickBy";
import keys from "lodash/keys";
import isFunction from "lodash/isFunction";

/**
 * @param {Number} target
 * @param {Number} lowerBound
 * @param {Number} upperBound
 * @param {Boolean} inclusive
 * @returns {Boolean}
 */
export const between = ( target, lowerBound, upperBound, inclusive = true ) => {
	if ( !inclusive ) {
		return target > lowerBound && target < upperBound;
	}
	return target >= lowerBound && target <= upperBound;
};

export class MultiSortCollection {
	filters = [];
	sorters = [];
	sortDirections = [];

	constructor( collection ) {
		this.collection = collection;
	}

	pipe( filter = () => true, sorter = () => 1, sortDirection = "ASC" ) {
		this.filters.push( filter );
		this.sorters.push( sorter );
		this.sortDirections.push( sortDirection );
	}

	sort() {
		const collection = [ ...this.collection, ];
		if ( this.filters.length === 0 ) {
			return collection;
		}
		let combinedCollection = [];
		this.filters.forEach( ( filter, index ) => {
			const sorter = this.sorters[ index ];
			const sortDirection = this.sortDirections[ index ];
			const filteredCollection = remove( collection, filter );
			const sortedCollection = orderBy( filteredCollection, [ sorter, ], [ sortDirection, ] );
			combinedCollection = combinedCollection.concat( sortedCollection );
		} );
		return combinedCollection;
	}
}

export const isSame = ( array1, array2, algorithm = identity ) => {
	return isEqual(
		sortBy( [ ...array1, ], algorithm ),
		sortBy( [ ...array2, ], algorithm )
	);
};

export const keysBy = ( object, filter = identity ) => {
	const filteredObject = pickBy( object, filter );
	return keys( filteredObject );
};

/**
 * Get all keys associated with truthy values form object
 * @param {Object} object
 * @returns {Array.<String>}
 */
export const truthyKeys = object => {
	return keysBy( object, value => !!value );
};

/**
 * Filter a collection by predicate, and receive both the
 *   filtered results and the remaining results
 * @param {Array} collection
 * @param {Function} predicate
 * @returns {{result: [], rest: []}}
 */
export const flitter = ( collection, predicate ) => {
	const result = [];
	const rest = [];
	for ( const item of collection ) {
		if ( predicate( item ) ) {
			result.push( item );
		} else {
			rest.push( item );
		}
	}
	return { result, rest, };
};

/**
 * Update (by comparator) and concat the difference of two collections
 * @param {Array} collection1
 * @param {Array} collection2
 * @param {Function} [comparator] = identity
 */
export const upcat = ( collection1, collection2, comparator = identity ) => {
	const result = [];
	// array will be mutated, so clone it first
	const otherCollection = [ ...collection2, ];

	const update = item => {
		let index = 0;
		for ( const otherItem of otherCollection ) {
			if ( comparator( item, otherItem ) ) {
				// keep the item from otherCollection,
				// disregard the item from collection1
				result.push( otherItem );
				otherCollection.splice( index, 1 );
				return;
			}
			index += 1;
		}
		// otherwise, keep the item from collection1
		result.push( item );
	};

	for ( const item of collection1 ) {
		update( item );
	}

	// pick up any remaining items and place them at the end
	return result.concat( otherCollection );
};

/**
 * Patch target object by applying patches in sequence. New object is returned.
 * @param {Object} target
 * @param {Array.<Object|Function.<target, Object>>} patches
 * @returns {Object}
 */
export const applyPatches = ( target, patches ) => {
	let patched = { ...target, };
	for ( const patch of patches ) {
		if ( isFunction( patch ) ) {
			patched = {
				...patched,
				...patch( target ),
			};
		} else {
			patched = {
				...patched,
				...patch,
			};
		}
	}
	return patched;
};



// WEBPACK FOOTER //
// ./src/lib/nodash.js